import React, { useState, useEffect } from 'react';
import './App.css';
import './Components/GlobalStyles.css'; // Import global styles first
import './Components/Home_comp/Navbar.css'; // Import component styles
import './Components/Home_comp/Footer.css'; // Import component styles
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';

import Navbar from './Components/Home_comp/Navbar';
import Home from './Components/Home_comp/Home';
import About from './Components/About/About';
import Contact from './Components/Contact/Contact';
import CarouselGallery from './Components/Event_Gallery/Event_Gallery';
import Footer from './Components/Home_comp/Footer';
import AuthForm from './login_regi/AuthForm';
import BookingSystem from './Components/Bookingsection/BookingSystem';
import BookingHistory from './Components/Bookingsection/BookingHistory';
import BookingSuccess from './Components/Bookingsection/BookingSuccess';
import PaymentTest from './Components/Bookingsection/PaymentTest';
import AdminDashboard from './Admin/AdminDashboard';
import Unauthorized from './common/Unauthorized';
import AdminLayout from './Admin/AdminLayout';
import { getToken, isValidToken } from './services/tokenService';
import ManageFunctions from './Admin/ManageFunctions';
import ManageSubFunctions from './Admin/ManageSubFunctions';
import ManageSubFunctionItems from './Admin/ManageSubFunctionItems';
import ChangeUserRole from './Admin/ChangeUserRole';
import ManageBookings from './Admin/ManageBookings';
import ManageUsers from './Admin/ManageUsers';
import EventManagement from './Admin/EventManagement';

function App() {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    role: null,
    isLoading: true // Add loading state
  });

  // Check authentication status on mount and when token changes
  useEffect(() => {
    const checkAuth = () => {
      const token = getToken();
      if (token && isValidToken()) {
        const role = localStorage.getItem('userRole');
        setAuthState({
          isAuthenticated: true,
          role,
          isLoading: false
        });
      } else {
        // Clean up invalid state
        if (token) {
          localStorage.removeItem('jwtToken');
          localStorage.removeItem('userRole');
          sessionStorage.removeItem('user');
        }
        setAuthState({
          isAuthenticated: false,
          role: null,
          isLoading: false
        });
      }
    };

    checkAuth();

    // Listen for storage events to keep auth state in sync across tabs
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const handleLoginSuccess = (role) => {
    setAuthState({
      isAuthenticated: true,
      role: role,
      isLoading: false
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('userRole');
    sessionStorage.removeItem('user');
    setAuthState({
      isAuthenticated: false,
      role: null,
      isLoading: false
    });

    // Force navigation to home page after logout
    window.location.href = '/';
  };

  // Enhanced ProtectedRoute component
  const ProtectedRoute = ({ children, allowedRoles }) => {
    // Use both state and localStorage for better reliability
    const token = getToken();
    const isAuth = token && isValidToken();
    const role = localStorage.getItem('userRole');

    if (!isAuth) {
      // Not authenticated at all
      return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
    }

    if (!allowedRoles.includes(role)) {
      // Authenticated but wrong role
      return <Navigate to="/unauthorized" replace />;
    }

    return children;
  };

  // User ProtectedRoute for user-only areas
  const UserProtectedRoute = ({ children }) => {
    const token = getToken();
    const isAuth = token && isValidToken();

    if (!isAuth) {
      return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
    }

    return children;
  };

  // For restricting the login page from authenticated users
  const AuthRedirect = () => {
    const role = localStorage.getItem('userRole');
    const token = getToken();
    const isAuth = token && isValidToken();

    if (isAuth) {
      if (role === 'ROLE_ADMIN' || role === 'ROLE_SUBADMIN') {
        return <Navigate to="/admin/dashboard" replace />;
      } else {
        return <Navigate to="/" replace />;
      }
    }

    return <AuthForm onLoginSuccess={handleLoginSuccess} />;
  };

  // Show loading state while checking auth
  if (authState.isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <Routes>
        {/* Public layout with Navbar and Footer */}
        <Route element={
          <>
            <Navbar
              isAuthenticated={authState.isAuthenticated}
              userRole={authState.role}
              onLogout={handleLogout}
            />
            <Outlet />
            <Footer />
          </>
        }>
          <Route path="/" element={<Home isAuthenticated={authState.isAuthenticated} />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/event-gallery" element={<CarouselGallery />} />

          {/* Redirect already authenticated users from login page */}
          <Route path="/login" element={<AuthRedirect />} />

          {/* Protected user routes */}
          <Route path="/booking" element={
            <UserProtectedRoute>
              <BookingSystem />
            </UserProtectedRoute>
          } />
          <Route path="/my-bookings" element={
            <UserProtectedRoute>
              <BookingHistory />
            </UserProtectedRoute>
          } />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/payment-test" element={<PaymentTest />} />
        </Route>

        {/* Admin layout with ProtectedRoute */}
        <Route element={
          <ProtectedRoute allowedRoles={['ROLE_ADMIN', 'ROLE_SUBADMIN']}>
            <AdminLayout onLogout={handleLogout} />
          </ProtectedRoute>
        }>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/bookings" element={<ManageBookings />} />
          <Route path="/admin/users" element={<ManageUsers />} />
          <Route path="/admin/change-role" element={<ChangeUserRole />} />
          <Route path="/admin/events" element={<EventManagement />}>
            <Route path="functions" element={<ManageFunctions />} />
            <Route path="subfunctions" element={<ManageSubFunctions />} />
            <Route path="items" element={<ManageSubFunctionItems />} />
          </Route>
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Catch all route for 404 */}
        <Route path="*" element={
          <>
            <Navbar
              isAuthenticated={authState.isAuthenticated}
              userRole={authState.role}
              onLogout={handleLogout}
            />
            <div className="not-found">
              <h2>Page Not Found</h2>
              <p>The page you are looking for does not exist.</p>
            </div>
            <Footer />
          </>
        } />
      </Routes>
    </Router>
  );
}

export default App;